import { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { AdminProviderProps, InitiativeData, InitiativeDataContextType } from '../services/types';
import * as Sentry from '@sentry/react';

// Configure axios to retry failed requests
axiosRetry(axios, {
    retries: 3, // Number of retry attempts
    retryDelay: axiosRetry.exponentialDelay, // Exponential backoff delay
    retryCondition: (error) => {
        return axiosRetry.isNetworkOrIdempotentRequestError(error); // Retry on network or 5xx server errors
    },
});

const InitiativeDataContext = createContext<InitiativeDataContextType>({
    initiatives: [],
    fetchInitiatives: () => {},
});

export const InitiativeDataProvider = ({ children }: AdminProviderProps) => {
    const [initiatives, setInitiatives] = useState<InitiativeData[]>([]);
    const apiUrl = `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getinitiatives`;

    const fetchInitiatives = async () => {
        try {
            const response = await axios.get(apiUrl);
            if (response.status === 200) {
                const data = response.data.data;
                setInitiatives(data);
            } else {
                console.error('Error fetching Initiatives:', response.data);
            }
        } catch (error) {
            // Specific error handling for Axios network errors
            if (axios.isAxiosError(error)) {
                // Check for network error or timeout
                if (error.code === 'ECONNABORTED') {
                    Sentry.captureMessage('Request Timeout: The request took too long to complete', 'warning');
                } else if (error.message.includes('Network Error')) {
                    Sentry.captureMessage('Network Error: Unable to reach the server', 'error');
                }

                // Log additional details like the URL and request payload
                Sentry.withScope(scope => {
                    scope.setContext('Axios Request', { url: apiUrl });
                    scope.setTag('error_code', error.code || 'UNKNOWN');
                    Sentry.captureException(error);
                });

                console.error('Error fetching outcomes:', error);
            } else {
                // Handle non-Axios errors (e.g., JavaScript errors)
                Sentry.captureException(error);
                console.error('Unexpected error fetching outcomes:', error);
            }
        }
    };

    useEffect(() => {
        fetchInitiatives();
    }, []);

    return (
        <InitiativeDataContext.Provider value={{ initiatives, fetchInitiatives }}>
            {children}
        </InitiativeDataContext.Provider>
    );
};

export default InitiativeDataContext;
